'use client';

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { sellerOrderApi } from '../../services/seller/orderApi';
import {
  Search,
  Refresh,
  FilterList,
  Visibility,
  Edit,
  Delete,
  Add,
  Close,
  CheckCircle,
  Cancel,
  Warning,
  Info,
  TrendingUp,
  TrendingDown,
  AccessTime,
  AttachMoney,
  Person,
  Email,
  Phone,
  LocationOn,
  ShoppingCart,
  Payment,
  Schedule,
  ZoomIn,
  ZoomOut
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed': return 'bg-green-100 text-green-800';
    case 'processing': return 'bg-blue-100 text-blue-800';
    case 'pending': return 'bg-yellow-100 text-yellow-800';
    case 'cancelled': return 'bg-red-100 text-red-800';
    case 'refunded': return 'bg-gray-100 text-gray-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const getStatusLabel = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed': return 'Hoàn thành';
    case 'processing': return 'Đang xử lý';
    case 'pending': return 'Chờ xử lý';
    case 'cancelled': return 'Đã hủy';
    case 'refunded': return 'Hoàn tiền';
    default: return status || 'Không xác định';
  }
};

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return '✅';
      case 'processing': return '🚚';
      case 'pending': return '⏰';
      case 'cancelled': return '❌';
      case 'refunded': return '⚠️';
      default: return '📦';
    }
  };

  // Design Case Status Helper Functions
  const getDesignCaseStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'in_progress': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'waiting_approval': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'approved': return 'bg-green-100 text-green-800 border-green-200';
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getDesignCaseStatusLabel = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': return 'Chờ xử lý';
      case 'in_progress': return 'Đang thực hiện';
      case 'waiting_approval': return 'Chờ duyệt';
      case 'approved': return 'Đã duyệt';
      case 'completed': return 'Hoàn thành';
      case 'cancelled': return 'Đã hủy';
      default: return status || 'Không xác định';
    }
  };

  const getDesignCaseStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': return '⏰';
      case 'in_progress': return '🎨';
      case 'waiting_approval': return '👀';
      case 'approved': return '✅';
      case 'completed': return '✅';
      case 'cancelled': return '❌';
      default: return '📋';
    }
  };



const SellerOrders = () => {
  const { user, token } = useAuth();
  const [allOrders, setAllOrders] = useState([]); // Tất cả đơn hàng từ API
  const [filteredOrders, setFilteredOrders] = useState([]); // Đơn hàng đã lọc theo ngày
  const [orders, setOrders] = useState([]); // Đơn hàng hiển thị trên trang hiện tại
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetail, setShowOrderDetail] = useState(false);
  const [editingCost, setEditingCost] = useState(false);
  const [newCost, setNewCost] = useState('');
  
  // Design Case Modal State
  const [showDesignCaseModal, setShowDesignCaseModal] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [designCost, setDesignCost] = useState('0');
  const [deadline, setDeadline] = useState('');
  const [priority, setPriority] = useState('Trung bình');
  const [notes, setNotes] = useState('');
  const [isCreatingCase, setIsCreatingCase] = useState(false);
  
  // Design Case Status State
  const [orderDesignCases, setOrderDesignCases] = useState({});
  const [loadingDesignCases, setLoadingDesignCases] = useState({});
  
  // Image Popup State
  const [showImagePopup, setShowImagePopup] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const [selectedImageAlt, setSelectedImageAlt] = useState('');
  const [imageScale, setImageScale] = useState(1);
  
  // Date range state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isCustomFilter, setIsCustomFilter] = useState(false); // true: custom filter, false: today filter
  const [filterType, setFilterType] = useState('today'); // today, week, month, year, custom, all
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [ordersPerPage, setOrdersPerPage] = useState(10);

  // Helper function to get order design case summary
  const getOrderDesignCaseSummary = (orderId) => {
    const designCaseData = orderDesignCases[orderId];
    if (!designCaseData) return null;

    const { cases, orderStatus } = designCaseData;
    
    if (!cases || cases.length === 0) {
      return {
        status: 'no_case',
        label: 'Chưa có design case',
        color: 'bg-gray-100 text-gray-800 border-gray-200',
        icon: '📋'
      };
    }

    // Tìm case có status cao nhất (ưu tiên: in_progress > waiting_approval > pending > approved > completed)
    const statusPriority = {
      'in_progress': 4,
      'waiting_approval': 3,
      'pending': 2,
      'approved': 1,
      'completed': 1,
      'cancelled': 0
    };

    const highestPriorityCase = cases.reduce((highest, current) => {
      const currentPriority = statusPriority[current.status] || 0;
      const highestPriority = statusPriority[highest.status] || 0;
      return currentPriority > highestPriority ? current : highest;
    });

    return {
      status: highestPriorityCase.status,
      label: getDesignCaseStatusLabel(highestPriorityCase.status),
      color: getDesignCaseStatusColor(highestPriorityCase.status),
      icon: getDesignCaseStatusIcon(highestPriorityCase.status),
      caseCount: cases.length
    };
  };

  // Load data khi component mount
  useEffect(() => {
    if (token) {
      // Mặc định lọc theo ngày hôm nay khi vào trang
      const today = new Date().toISOString().split('T')[0];
      setStartDate(today);
      setEndDate(today);
      setIsCustomFilter(false);
      setFilterType('today');
      loadData();
    }
  }, [token]);

  // Lọc đơn hàng theo ngày và tìm kiếm
  const filterOrdersByDate = (orders, startDate, endDate, isCustom, filterType, searchTerm) => {
    if (!orders || orders.length === 0) return [];
    
    // Lọc theo tìm kiếm
    let filteredBySearch = orders;
    if (searchTerm.trim()) {
      try {
        // Ensure searchTerm is always a string
        const searchTermLower = (searchTerm || '').toLowerCase();
        
        filteredBySearch = orders.filter(order => {
          const matchesSearch = 
            (order.order_id ? order.order_id.toString() : '').includes(searchTermLower) ||
            (order.customer_name || '').toLowerCase().includes(searchTermLower) ||
            (order.customer_email || '').toLowerCase().includes(searchTermLower) ||
            (order.phone || '').toLowerCase().includes(searchTermLower) ||
            (order.payment_method || '').toLowerCase().includes(searchTermLower);
          
          return matchesSearch;
        });
      } catch (error) {
        console.error('Error filtering orders:', error);
        filteredBySearch = orders;
      }
    }
    
    if (isCustom && startDate && endDate) {
      // Lọc theo ngày tùy chỉnh
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999); // Set đến cuối ngày
      
      return filteredBySearch.filter(order => {
        const orderDate = new Date(order.date_created || order.created_at);
        return orderDate >= start && orderDate <= end;
      });
    } else {
      // Lọc theo filterType
      const now = new Date();
      let startOfPeriod, endOfPeriod;
      
      switch (filterType) {
        case 'today':
          startOfPeriod = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          endOfPeriod = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
          break;
        case 'week':
          // Tuần hiện tại (từ thứ 2 đến chủ nhật)
          const dayOfWeek = now.getDay();
          const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
          startOfPeriod = new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysFromMonday);
          endOfPeriod = new Date(startOfPeriod.getFullYear(), startOfPeriod.getMonth(), startOfPeriod.getDate() + 6, 23, 59, 59, 999);
          break;
        case 'month':
          // Tháng hiện tại
          startOfPeriod = new Date(now.getFullYear(), now.getMonth(), 1);
          endOfPeriod = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
          break;
        case 'year':
          // Năm hiện tại
          startOfPeriod = new Date(now.getFullYear(), 0, 1);
          endOfPeriod = new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999);
          break;
        default:
          // Mặc định là hôm nay
          startOfPeriod = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          endOfPeriod = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
      }
      
      return filteredBySearch.filter(order => {
        const orderDate = new Date(order.date_created || order.created_at);
        return orderDate >= startOfPeriod && orderDate <= endOfPeriod;
      });
    }
  };

  // Lọc đơn hàng khi allOrders hoặc filter thay đổi
  useEffect(() => {
    const filtered = filterOrdersByDate(allOrders, startDate, endDate, isCustomFilter, filterType, searchTerm);
    setFilteredOrders(filtered);
    setCurrentPage(1); // Reset về trang đầu khi filter
  }, [allOrders, startDate, endDate, isCustomFilter, filterType, searchTerm]);

  // Tính toán đơn hàng hiển thị khi filteredOrders hoặc currentPage thay đổi
  useEffect(() => {
    const startIndex = (currentPage - 1) * ordersPerPage;
    const endIndex = startIndex + ordersPerPage;
    const currentOrders = filteredOrders.slice(startIndex, endIndex);
    
    setOrders(currentOrders);
    setTotalPages(Math.ceil(filteredOrders.length / ordersPerPage));
    setTotalOrders(filteredOrders.length);
  }, [filteredOrders, currentPage, ordersPerPage]);

  const loadData = async () => {
    console.log('loadData function called');
    try {
      setLoading(true);
      setError(null);
      
      console.log('Đang gọi API seller orders...');
      
      // Xác định ngày để gọi API
      let apiStartDate = null;
      let apiEndDate = null;
      
      if (isCustomFilter && startDate && endDate) {
        apiStartDate = startDate;
        apiEndDate = endDate;
      } else {
        // Nếu không có ngày tùy chỉnh, sử dụng ngày hiện tại
        const today = new Date().toISOString().split('T')[0];
        apiStartDate = today;
        apiEndDate = today;
      }
      
      console.log('API Parameters:', { startDate: apiStartDate, endDate: apiEndDate });
      
      // Gọi API với tham số ngày
      const response = await sellerOrderApi.getSellerOrders(token, apiStartDate, apiEndDate);
      
      console.log('=== API RESPONSE DEBUG ===');
      console.log('API Response:', response);
      console.log('API Response type:', typeof response);
      console.log('API Response keys:', response ? Object.keys(response) : 'null');
      console.log('Is Array?', Array.isArray(response));
      console.log('Response length:', response ? (Array.isArray(response) ? response.length : 'not array') : 'null');
      
      if (response && Array.isArray(response) && response.length > 0) {
        console.log('First order sample:', response[0]);
        console.log('First order keys:', Object.keys(response[0]));
      }
      console.log('=== END API RESPONSE DEBUG ===');
      
      // Xử lý response từ backend API thực
      let ordersToSet = [];
      
      if (response && response.success && response.orders) {
        console.log('Setting orders from response.success.orders:', response.orders.length);
        ordersToSet = response.orders;
      } else if (response && Array.isArray(response)) {
        console.log('Setting orders from array response:', response.length);
        ordersToSet = response;
      } else if (response && response.data && Array.isArray(response.data)) {
        console.log('Setting orders from response.data:', response.data.length);
        ordersToSet = response.data;
      } else if (response && response.orders && Array.isArray(response.orders)) {
        console.log('Setting orders from response.orders:', response.orders.length);
        ordersToSet = response.orders;
      } else {
        console.error('Invalid API response format:', response);
        setError('Dữ liệu API không đúng định dạng');
        ordersToSet = [];
      }
      
      console.log('Final orders to set:', ordersToSet.length);
      if (ordersToSet.length > 0) {
        console.log('Sample order structure:', ordersToSet[0]);
      }
      
      setAllOrders(ordersToSet);
      
    } catch (error) {
      console.error('Error loading data:', error);
      setError(`Lỗi khi tải dữ liệu: ${error.message}`);
      setAllOrders([]);
    } finally {
      setLoading(false);
    }
  };

  // Load design case status for a specific order
  const loadOrderDesignCaseStatus = async (orderId) => {
    if (orderDesignCases[orderId]) {
      return; // Already loaded
    }
    
    try {
      setLoadingDesignCases(prev => ({ ...prev, [orderId]: true }));
      const response = await sellerOrderApi.getOrderDesignCaseStatus(token, orderId);
      
      if (response && response.success && response.data) {
        setOrderDesignCases(prev => ({
          ...prev,
          [orderId]: response.data
        }));
      }
    } catch (error) {
      console.error(`Error loading design case status for order ${orderId}:`, error);
      // Don't show error toast for this as it's not critical
    } finally {
      setLoadingDesignCases(prev => ({ ...prev, [orderId]: false }));
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setCurrentPage(1); // Reset về trang đầu
    await loadData();
    setRefreshing(false);
    toast.success('Đã làm mới dữ liệu');
  };

  const handleDateFilter = async () => {
    // Kiểm tra nếu có ngày bắt đầu và kết thúc
    if (startDate && endDate) {
      setIsCustomFilter(true);
      setFilterType('custom');
      toast.success('Đã lọc theo ngày tùy chỉnh');
    } else {
      // Nếu không có ngày thì lọc theo hôm nay
      setIsCustomFilter(false);
      setStartDate('');
      setEndDate('');
      setFilterType('today');
      toast.success('Đã lọc theo ngày hôm nay');
    }
  };

  const handleFilterChange = (type) => {
    setFilterType(type);
    setIsCustomFilter(false);
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
    
    let message = '';
    switch (type) {
      case 'today':
        message = 'Đã lọc theo ngày hôm nay';
        break;
      case 'week':
        message = 'Đã lọc theo tuần này';
        break;
      case 'month':
        message = 'Đã lọc theo tháng này';
        break;
      case 'year':
        message = 'Đã lọc theo năm này';
        break;
      default:
        message = 'Đã lọc dữ liệu';
    }
    
    toast.success(message);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(parseFloat(amount));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calculate total items across all orders
  const calculateTotalItems = (orders) => {
    return orders.reduce((total, order) => {
      if (order.products) {
        try {
          const products = parseProducts(order.products);
          return total + products.reduce((sum, product) => sum + (product.quantity || product.qty || 1), 0);
        } catch (error) {
          console.error('Error parsing products for order:', order.order_id, error);
          return total;
        }
      }
      return total;
    }, 0);
  };

  const handleOrderClick = (order) => {
    setSelectedOrder(order);
    setShowOrderDetail(true);
  };

  const parseProducts = (productsJson) => {
    try {
      if (!productsJson) return [];
      return JSON.parse(productsJson);
    } catch (error) {
      console.error('Error parsing products:', error);
      return [];
    }
  };

  const getProductVariation = (product) => {
    if (product.variation_attributes && Object.keys(product.variation_attributes).length > 0) {
      return Object.entries(product.variation_attributes)
        .map(([key, value]) => `${key.replace('attribute_', '').replace('pa_', '')}: ${value}`)
        .join(', ');
    }
    
    if (product.meta_data) {
      const variations = product.meta_data
        .filter(meta => meta.key.includes('attribute_') || meta.key.includes('pa_'))
        .map(meta => `${meta.key.replace('attribute_', '').replace('pa_', '')}: ${meta.value}`)
        .join(', ');
      
      return variations;
    }
    
    return '';
  };

  const handleCostUpdate = async () => {
    if (!newCost || parseFloat(newCost) < 0) {
      toast.error('Chi phí phải là số dương');
      return;
    }

    try {
      // Gọi API để cập nhật cost
      await sellerOrderApi.updateOrderCost(token, selectedOrder.order_id, parseFloat(newCost));
      
      // Cập nhật local state
      setSelectedOrder({
        ...selectedOrder,
        cost: parseFloat(newCost)
      });
      
      // Cập nhật trong danh sách orders
      setAllOrders(prev => prev.map(order => 
        order.order_id === selectedOrder.order_id 
          ? { ...order, cost: parseFloat(newCost) }
          : order
      ));
      
      setEditingCost(false);
      setNewCost('');
      toast.success('Đã cập nhật chi phí thành công');
    } catch (error) {
      console.error('Error updating cost:', error);
      toast.error('Lỗi khi cập nhật chi phí');
    }
  };

  const handleProductSelection = (productId, isSelected) => {
    if (isSelected) {
      // Thêm sản phẩm vào danh sách đã chọn
      const product = parseProducts(selectedOrder.products).find(p => p.product_id === productId);
      if (product) {
        setSelectedProducts(prev => [...prev, product]);
      }
    } else {
      // Xóa sản phẩm khỏi danh sách đã chọn
      setSelectedProducts(prev => prev.filter(p => p.product_id !== productId));
    }
  };

  const handleCreateDesignCase = () => {
    if (selectedProducts.length === 0) {
      toast.error('Vui lòng chọn ít nhất một sản phẩm');
      return;
    }
    setShowDesignCaseModal(true);
  };

  const handleSubmitDesignCase = async () => {
    if (!designCost || parseFloat(designCost) <= 0) {
      toast.error('Vui lòng nhập chi phí thiết kế hợp lệ');
      return;
    }

    setIsCreatingCase(true);
    try {
      // Lấy product_ids từ selectedProducts
      const productIds = selectedProducts.map(p => p.product_id);
      
    
      const caseData = {
        order_id: selectedOrder.order_id,
        product_ids: productIds,
        cost_per_design: parseFloat(designCost),
        deadline: deadline ? new Date(deadline).toISOString() : null,
        note: notes,
        priority: priority === 'Thấp' ? 'low' : 
                 priority === 'Trung bình' ? 'medium' : 
                 priority === 'Cao' ? 'high' : 'urgent'
      };

      console.log('Sending design case data:', caseData);
      await sellerOrderApi.createDesignCase(token, caseData);
      
      toast.success('Đã tạo design case thành công!');
      setShowDesignCaseModal(false);
      setSelectedProducts([]);
      setDesignCost('0');
      setDeadline('');
      setPriority('Trung bình');
      setNotes('');
    } catch (error) {
      console.error('Error creating design case:', error);
      toast.error(`Lỗi khi tạo design case: ${error.response?.data?.message || error.message}`);
    } finally {
      setIsCreatingCase(false);
    }
  };

  const resetDesignCaseModal = () => {
    setShowDesignCaseModal(false);
    setSelectedProducts([]);
    setDesignCost('0');
    setDeadline('');
    setPriority('Trung bình');
    setNotes('');
    setIsCreatingCase(false);
  };

  // Image Popup Handlers
  const handleImageClick = (imageUrl, altText) => {
    setSelectedImage(imageUrl);
    setSelectedImageAlt(altText);
    setImageScale(1);
    setShowImagePopup(true);
  };

  const handleImageWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    const newScale = Math.max(0.5, Math.min(3, imageScale + delta));
    setImageScale(newScale);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl shadow-lg mb-6"
        >
          <div className="px-6 py-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 space-y-2 sm:space-y-0">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-white mb-2">Quản lý đơn hàng</h1>
                <p className="text-blue-100 text-sm">Theo dõi và quản lý tất cả đơn hàng của bạn</p>
              </div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm text-white rounded-lg hover:bg-white/30 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {refreshing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Đang tải...</span>
                  </>
                ) : (
                  <>
                    <Refresh className="w-4 h-4" />
                    <span>Làm mới</span>
                  </>
                )}
              </motion.button>
            </div>
          </div>

          {/* Statistics */}
          <div className="px-6 py-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="bg-white/20 backdrop-blur-sm rounded-lg p-4 border border-white/30"
              >
                <div className="flex items-center">
                  <div className="p-1 lg:p-2 bg-blue-200 rounded-lg">
                    <ShoppingCart className="w-3 h-3 lg:w-5 lg:h-5 text-blue-600" />
                  </div>
                  <div className="ml-2 lg:ml-3">
                    <p className="text-xs lg:text-sm font-medium text-blue-100">Tổng Đơn</p>
                    <p className="text-lg lg:text-xl font-bold text-white">{totalOrders}<span className="text-xs text-blue-200">({calculateTotalItems(filteredOrders)} item)</span></p>
                    
                  </div>
                </div>
              </motion.div>

              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="bg-white/20 backdrop-blur-sm rounded-lg p-4 border border-white/30"
              >
                <div className="flex items-center">
                  <div className="p-1 lg:p-2 bg-green-200 rounded-lg">
                    <AttachMoney className="w-3 h-3 lg:w-5 lg:h-5 text-green-600" />
                  </div>
                  <div className="ml-2 lg:ml-3">
                    <p className="text-xs lg:text-sm font-medium text-green-100">Doanh thu</p>
                    <p className="text-lg lg:text-xl font-bold text-white">
                      {formatCurrency(filteredOrders.reduce((sum, order) => sum + parseFloat(order.total || 0), 0))}
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="bg-white/20 backdrop-blur-sm rounded-lg p-4 border border-white/30"
              >
                <div className="flex items-center">
                  <div className="p-1 lg:p-2 bg-yellow-200 rounded-lg">
                    <CheckCircle className="w-3 h-3 lg:w-5 lg:h-5 text-yellow-600" />
                  </div>
                  <div className="ml-2 lg:ml-3">
                    <p className="text-xs lg:text-sm font-medium text-yellow-100">Đã hoàn thành</p>
                    <p className="text-lg lg:text-xl font-bold text-white">
                      {filteredOrders.filter(order => order.order_status === 'completed').length}
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="bg-white/20 backdrop-blur-sm rounded-lg p-4 border border-white/30"
              >
                <div className="flex items-center">
                  <div className="p-1 lg:p-2 bg-orange-200 rounded-lg">
                    <Schedule className="w-3 h-3 lg:w-5 lg:h-5 text-orange-600" />
                  </div>
                  <div className="ml-2 lg:ml-3">
                    <p className="text-xs lg:text-sm font-medium text-orange-100">Đang xử lý</p>
                    <p className="text-lg lg:text-xl font-bold text-white">
                      {filteredOrders.filter(order => order.order_status === 'processing').length}
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-lg mb-6">
          <div className="p-4">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Search Input */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="🔍 Tìm kiếm theo ID đơn hàng, tên khách hàng, email..."
                    value={searchTerm}
                    onChange={(e) => {
                      try {
                        setSearchTerm(e.target.value || '');
                      } catch (error) {
                        console.error('Error updating search term:', error);
                        setSearchTerm('');
                      }
                    }}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              
              <div className="flex gap-2 w-full lg:w-auto">
                {/* Date Filter */}
                <select
                  value={isCustomFilter ? 'custom' : filterType}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === 'custom') {
                      setIsCustomFilter(true);
                      setFilterType('custom');
                    } else {
                      setIsCustomFilter(false);
                      setFilterType(value);
                      setStartDate('');
                      setEndDate('');
                    }
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent flex-1 lg:w-48"
                >
                  <option value="today">📅 Hôm nay</option>
                  <option value="week">📊 Tuần này</option>
                  <option value="month">📈 Tháng này</option>
                  <option value="year">📅 Năm này</option>
                  <option value="custom">🔍 Tùy chỉnh</option>
                </select>
              </div>

              {/* Custom Date Range - chỉ hiển thị khi chọn "Tùy chỉnh" */}
              {isCustomFilter && (
                <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <span className="text-sm text-gray-500 self-center">đến</span>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleDateFilter}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                  >
                    <Search className="w-4 h-4" />
                    <span>Lọc</span>
                  </motion.button>
                </div>
              )}
            </div>
          </div>
        </div>
        </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Lỗi khi tải dữ liệu
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

        {/* Orders Table */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Đang tải dữ liệu...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-blue-600 to-purple-600 sticky top-0 z-30 shadow-lg">
                  <tr>
                    <th className="px-2 sm:px-3 md:px-4 lg:px-6 py-2 sm:py-3 md:py-4 lg:py-5 text-left text-xs font-bold text-white uppercase tracking-wider sticky left-0 bg-gradient-to-r from-blue-600 to-purple-600 z-35">
                      ID
                    </th>
                    <th className="px-2 sm:px-3 md:px-4 lg:px-6 py-2 sm:py-3 md:py-4 lg:py-5 text-left text-xs font-bold text-white uppercase tracking-wider">
                      Khách hàng
                    </th>
                    <th className="px-2 sm:px-3 md:px-4 lg:px-6 py-2 sm:py-3 md:py-4 lg:py-5 text-left text-xs font-bold text-white uppercase tracking-wider min-w-[120px] sm:min-w-[140px]">
                      Tổng tiền
                    </th>
                    <th className="px-2 sm:px-3 md:px-4 lg:px-6 py-2 sm:py-3 md:py-4 lg:py-5 text-left text-xs font-bold text-white uppercase tracking-wider min-w-[120px] sm:min-w-[140px]">
                      Chi phí
                    </th>
                    <th className="px-2 sm:px-3 md:px-4 lg:px-6 py-2 sm:py-3 md:py-4 lg:py-5 text-left text-xs font-bold text-white uppercase tracking-wider min-w-[120px] sm:min-w-[140px]">
                      Trạng thái
                    </th>
                    <th className="px-2 sm:px-3 md:px-4 lg:px-6 py-2 sm:py-3 md:py-4 lg:py-5 text-left text-xs font-bold text-white uppercase tracking-wider min-w-[120px] sm:min-w-[140px]">
                      Design Case
                    </th>
                    <th className="px-2 sm:px-3 md:px-4 lg:px-6 py-2 sm:py-3 md:py-4 lg:py-5 text-left text-xs font-bold text-white uppercase tracking-wider">
                      Phương thức
                    </th>
                    <th className="px-2 sm:px-3 md:px-4 lg:px-6 py-2 sm:py-3 md:py-4 lg:py-5 text-left text-xs font-bold text-white uppercase tracking-wider">
                      Ngày tạo
                    </th>
                  </tr>
                </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                  {console.log('=== TABLE RENDER DEBUG ===')}
                  {console.log('Rendering table with orders:', orders.length)}
                  {console.log('Orders array:', orders)}
                  {console.log('First order fields:', orders.length > 0 ? Object.keys(orders[0]) : 'No orders')}
                  {console.log('=== END TABLE RENDER DEBUG ===')}
                  
                  {orders.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                        <div className="flex flex-col items-center">
                          <ShoppingCart className="w-12 h-12 text-gray-300 mb-4" />
                          <p className="text-lg font-medium">Không có đơn hàng</p>
                          <p className="text-sm">Chưa có đơn hàng nào được tìm thấy</p>
                          <p className="text-xs text-gray-400 mt-2">
                            Debug: allOrders={allOrders.length}, filtered={filteredOrders.length}, current={orders.length}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    orders.map((order, index) => (
                      <motion.tr 
                        key={order.id || order.order_id || index} 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        whileHover={{ 
                          backgroundColor: 'rgba(59, 130, 246, 0.05)',
                          scale: 1.01 
                        }}
                        className="cursor-pointer transition-all duration-300"
                        onClick={() => handleOrderClick(order)}
                      >
                        <td className="px-2 sm:px-3 md:px-4 lg:px-6 py-3 sm:py-4 md:py-5 lg:py-6 sticky left-0 bg-white z-20">
                          <div className="flex items-center">
                           
                            <div className="ml-2 sm:ml-4">
                              <div className="text-xs font-medium text-gray-900">
                                ID: {order.order_id || order.id || 'N/A'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-2 sm:px-3 md:px-4 lg:px-6 py-3 sm:py-4 md:py-5 lg:py-6">
                          <div className="text-xs font-medium text-gray-900 leading-tight">
                            {order.customer_name || order.customerName || order.name || 'N/A'}
                          </div>
                          <div className="text-xs font-medium text-gray-500">
                            {order.customer_email || order.customerEmail || order.email || 'N/A'}
                          </div>
                          <div className="text-xs font-medium text-gray-400">
                            {order.phone || order.phone_number || 'N/A'}
                          </div>
                        </td>
                        <td className="px-2 sm:px-3 md:px-4 lg:px-6 py-3 sm:py-4 md:py-5 lg:py-6 min-w-[120px] sm:min-w-[140px]">
                          <div className="text-xs font-bold text-blue-600">
                            {formatCurrency(order.total || order.amount || 0)}
                          </div>
                          <div className="text-xs font-medium text-gray-500">
                            Phí ship: {formatCurrency(order.shipping_fee || order.shipping_fee || 0)}
                          </div>
                        </td>
                        <td className="px-2 sm:px-3 md:px-4 lg:px-6 py-3 sm:py-4 md:py-5 lg:py-6 min-w-[120px] sm:min-w-[140px]">
                          <div className="text-xs font-bold text-red-600">
                            {formatCurrency(order.cost || order.production_cost || 0)}
                          </div>
                          <div className="text-xs font-medium text-gray-500">
                            Chi phí sản xuất
                          </div>
                        </td>
                        <td className="px-2 sm:px-3 md:px-4 lg:px-6 py-3 sm:py-4 md:py-5 lg:py-6 min-w-[120px] sm:min-w-[140px]">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.order_status || order.status || 'pending')}`}>
                            <span className="mr-1">{getStatusIcon(order.order_status || order.status || 'pending')}</span>
                            <span>{getStatusLabel(order.order_status || order.status || 'pending')}</span>
                          </span>
                        </td>
                        <td className="px-2 sm:px-3 md:px-4 lg:px-6 py-3 sm:py-4 md:py-5 lg:py-6 min-w-[120px] sm:min-w-[140px]">
                          {(() => {
                            const orderId = order.order_id || order.id;
                            const designCaseSummary = getOrderDesignCaseSummary(orderId);
                            
                            // Load design case data if not already loaded
                            if (!orderDesignCases[orderId] && !loadingDesignCases[orderId]) {
                              loadOrderDesignCaseStatus(orderId);
                            }
                            
                            if (loadingDesignCases[orderId]) {
                              return (
                                <div className="flex items-center justify-center">
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                </div>
                              );
                            }
                            
                            if (!designCaseSummary) {
                              return (
                                <div className="text-xs text-gray-500">
                                  <span className="inline-flex items-center px-2 py-1 rounded-full bg-gray-100 text-gray-800 border border-gray-200">
                                    <span className="mr-1">📋</span>
                                    <span>Chưa có case</span>
                                  </span>
                                </div>
                              );
                            }
                            
                            return (
                              <div className="space-y-1">
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${designCaseSummary.color}`}>
                                  <span className="mr-1">{designCaseSummary.icon}</span>
                                  <span>{designCaseSummary.label}</span>
                                </span>
                                {designCaseSummary.caseCount > 1 && (
                                  <div className="text-xs text-gray-500 text-center">
                                    {designCaseSummary.caseCount} cases
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-2 sm:px-3 md:px-4 lg:px-6 py-3 sm:py-4 md:py-5 lg:py-6">
                          <div className="text-xs font-medium text-gray-900">
                            {order.payment_method || order.paymentMethod || 'N/A'}
                          </div>
                        </td>
                        <td className="px-2 sm:px-3 md:px-4 lg:px-6 py-3 sm:py-4 md:py-5 lg:py-6">
                          <div className="text-xs font-medium text-gray-900">
                            {formatDate(order.date_created || order.created_at || order.dateCreated || order.createdAt || '')}
                          </div>
                        </td>
                      </motion.tr>
                    ))
                  )}
                </tbody>
          </table>
        </div>
        )}
      </div>

        {/* Pagination */}
        <div className="bg-white rounded-xl shadow-lg p-6 mt-6">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="text-sm text-gray-700">
                <span className="font-medium">Hiển thị {orders.length}</span> trong tổng số{' '}
                <span className="font-bold text-blue-600">{totalOrders}</span> đơn hàng
                {isCustomFilter ? (
                  <span className="text-blue-600 ml-2">
                    (Lọc theo ngày: {startDate} - {endDate})
                  </span>
                ) : (
                                  <span className="text-green-600 ml-2">
                  {filterType === 'today' && '(Lọc theo ngày hôm nay)'}
                    {filterType === 'week' && '(Lọc theo tuần này)'}
                    {filterType === 'month' && '(Lọc theo tháng này)'}
                    {filterType === 'year' && '(Lọc theo năm này)'}
                  </span>
                )}
                {searchTerm && (
                  <span className="text-orange-600 ml-2">
                    (Tìm kiếm: "{searchTerm}")
                  </span>
                )}
              </div>
              
              {/* Số lượng dòng mỗi trang */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Hiển thị:</span>
                <select
                  value={ordersPerPage}
                  onChange={(e) => {
                    setOrdersPerPage(parseInt(e.target.value));
                    setCurrentPage(1); // Reset về trang đầu khi thay đổi số lượng
                  }}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={10}>10</option>
                  <option value={30}>30</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-sm text-gray-600">dòng/trang</span>
              </div>
            </div>
              <nav className="flex items-center space-x-2">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  ← Trước
                </motion.button>
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <motion.button
                        key={pageNum}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-4 py-2 rounded-lg transition-colors ${
                          currentPage === pageNum
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        {pageNum}
                      </motion.button>
                    );
                  })}
                </div>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  Sau →
                </motion.button>
              </nav>
            </div>
          </div>


      {/* Order Detail Modal */}
      {showOrderDetail && selectedOrder && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
        >
          <motion.div 
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="fixed right-0 top-0 h-full w-full md:w-2/3 lg:w-1/2 xl:w-1/3 bg-white shadow-2xl overflow-hidden"
          >
            <div className="flex flex-col h-full">
              {/* Sticky Header */}
              <div className="flex justify-between items-center bg-gradient-to-r from-green-500 to-green-600 text-white p-4 shadow-lg sticky top-0 z-10">
                <h3 className="text-lg font-bold">
                  Chi tiết đơn hàng #{selectedOrder.order_id}
                </h3>
                <motion.button
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setShowOrderDetail(false)}
                  className="p-2 hover:bg-white/20 rounded-full transition-all duration-200"
                >
                  ✕
                </motion.button>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto p-4 space-y-6">
                {/* Order Information - Prioritized */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  className="bg-yellow-500 bg-opacity-30 rounded-xl p-4 border border-yellow-200"
                >
                  <motion.div 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.2 }}
                    className="flex justify-between items-start mb-3"
                  >
                    <h4 className="text-sm lg:text-md font-medium text-yellow-800">Thông tin đơn hàng</h4>
                    <motion.div 
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3, delay: 0.3 }}
                      className="text-right"
                    >
                    
                      <div className="text-sm font-semibold text-yellow-800 bg-yellow-100 px-3 py-1 rounded-lg border border-yellow-300">
                         {formatDate(selectedOrder.date_created)}
                      </div>
                    </motion.div>
                  </motion.div>
                                      <motion.div 
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.4, delay: 0.4 }}
                      className="grid grid-cols-1 sm:grid-cols-2 gap-2 lg:gap-4 w-full"
                    >
                      <motion.div
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: 0.5 }}
                      >
                        <label className="text-sm text-gray-600">Cửa hàng:</label>
                        <p className="text-sm font-medium">{selectedOrder.store || selectedOrder.storeName || 'N/A'}</p>
                      </motion.div>
                      <motion.div
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: 0.6 }}
                      >
                      <label className="text-sm text-gray-600">ID đơn hàng:</label>
                      <p className="text-sm font-medium">#{selectedOrder.order_id}</p>
                      </motion.div>
                      <motion.div
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: 0.7 }}
                      >
                      <label className="text-sm text-gray-600">Trạng thái:</label>
                      <div className="mt-1">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedOrder.order_status)}`}>
                          <span className="mr-1">{getStatusIcon(selectedOrder.order_status)}</span>
                          <span>{getStatusLabel(selectedOrder.order_status)}</span>
                        </span>
                      </div>
                      </motion.div>
                      <motion.div
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: 0.8 }}
                      >
                      <label className="text-sm text-gray-600">Phương thức thanh toán:</label>
                      <p className="text-sm font-medium">{selectedOrder.payment_method}</p>
                      </motion.div>
                      {/* Store Information within Order Details */}
                    </motion.div>
                  </motion.div>

                {/* Customer Information */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="bg-blue-500 bg-opacity-30 rounded-xl p-4 border border-blue-200"
                >
                  <motion.h4 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.3 }}
                    className="text-sm lg:text-md font-medium text-blue-800 mb-3"
                  >
                    Thông tin khách hàng
                  </motion.h4>
                  <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.4, delay: 0.4 }}
                    className="grid grid-cols-1 sm:grid-cols-2 gap-2 lg:gap-4 w-full"
                  >
                    <motion.div
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: 0.5 }}
                    >
                      <label className="text-sm text-gray-600">Tên:</label>
                      <p className="text-sm font-medium">{selectedOrder.customer_name}</p>
                    </motion.div>
                    <motion.div
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: 0.6 }}
                    >
                      <label className="text-sm text-gray-600">Email:</label>
                      <p className="text-sm font-medium">{selectedOrder.customer_email}</p>
                    </motion.div>
                    <motion.div
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: 0.7 }}
                    >
                      <label className="text-sm text-gray-600">Số điện thoại:</label>
                      <p className="text-sm font-medium">{selectedOrder.phone}</p>
                    </motion.div>
                    <motion.div
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: 0.8 }}
                    >
                      <label className="text-sm text-gray-600">Địa chỉ:</label>
                      <p className="text-sm font-medium">
                        {selectedOrder.address}, {selectedOrder.city}, {selectedOrder.state} {selectedOrder.zipcode}, {selectedOrder.country}
                      </p>
                    </motion.div>
                  </motion.div>
                </motion.div>

                {/* Financial Information */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className="bg-green-500 bg-opacity-30 rounded-xl p-4 border border-green-200"
                >
                  <motion.h4 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.4 }}
                    className="text-sm lg:text-md font-medium text-green-800 mb-3 flex justify-between items-center"
                  >
                      Thông tin tài chính
                      {(selectedOrder.cost === 0 || selectedOrder.cost === null) && (
                      <motion.button
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.3, delay: 0.5 }}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                          onClick={() => {
                            setEditingCost(true);
                            setNewCost('');
                          }}
                          className="px-3 py-1 bg-green-600 text-white rounded-lg text-xs hover:bg-green-700 transition-all duration-200 transform hover:scale-105"
                        >
                          ✏️ Cập nhật chi phí
                      </motion.button>
                      )}
                  </motion.h4>
                    
                    {/* Cost Edit Form */}
                    {editingCost && (
                      <motion.div 
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="mb-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200"
                      >
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            value={newCost}
                            onChange={(e) => setNewCost(e.target.value)}
                            placeholder="Nhập chi phí..."
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                            min="0"
                            step="0.01"
                          />
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={handleCostUpdate}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 transition-colors"
                          >
                            💾 Lưu
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => {
                              setEditingCost(false);
                              setNewCost('');
                            }}
                            className="px-4 py-2 bg-gray-500 text-white rounded-lg text-sm hover:bg-gray-600 transition-colors"
                          >
                            ❌ Hủy
                          </motion.button>
                        </div>
                      </motion.div>
                    )}
                    
                    <motion.div 
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.4, delay: 0.5 }}
                      className="grid grid-cols-1 sm:grid-cols-2 gap-2 lg:gap-4 w-full"
                    >
                      <motion.div
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: 0.6 }}
                      >
                        <label className="text-sm text-gray-600">Tổng tiền:</label>
                          <p className="text-sm lg:text-lg font-bold text-blue-600">{formatCurrency(selectedOrder.total)}</p>
                      </motion.div>
                      <motion.div
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: 0.7 }}
                      >
                          <label className="text-sm text-gray-600">Chi phí:</label>
                          <p className="text-sm font-medium text-red-600">{formatCurrency(selectedOrder.cost || 0)}</p>
                      </motion.div>
                      <motion.div
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: 0.8 }}
                      >
                          <label className="text-sm text-gray-600">Phí ship:</label>
                        <p className="text-sm font-medium">{formatCurrency(selectedOrder.shipping_fee || 0)}</p>
                      </motion.div>
                      <motion.div
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: 0.9 }}
                      >
                          <label className="text-sm text-gray-600">Lợi nhuận:</label>
                          <p className="text-sm font-bold text-green-600">{formatCurrency((parseFloat(selectedOrder.total || 0) - parseFloat(selectedOrder.cost || 0) - parseFloat(selectedOrder.shipping_fee || 0)))}</p>
                      </motion.div>
                    </motion.div>
                  </motion.div>

                                 {/* Products */}
                   <motion.div 
                     initial={{ opacity: 0, y: 20 }}
                     animate={{ opacity: 1, y: 0 }}
                     transition={{ duration: 0.5, delay: 0.4 }}
                     className="bg-purple-500 bg-opacity-20 rounded-xl p-4 border border-purple-200"
                   >
                     <motion.div 
                       initial={{ opacity: 0, x: -20 }}
                       animate={{ opacity: 1, x: 0 }}
                       transition={{ duration: 0.4, delay: 0.5 }}
                       className="flex justify-between items-center mb-3"
                     >
                       <h4 className="text-sm lg:text-md font-medium text-purple-800">Sản phẩm đã đặt</h4>
                       <motion.button
                         initial={{ opacity: 0, scale: 0.8 }}
                         animate={{ opacity: 1, scale: 1 }}
                         transition={{ duration: 0.3, delay: 0.6 }}
                         whileHover={{ scale: 1.05 }}
                         whileTap={{ scale: 0.95 }}
                         onClick={handleCreateDesignCase}
                         disabled={selectedProducts.length === 0}
                         className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 transition-all duration-200 transform hover:scale-105 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                       >
                         🎨 Tạo Design Case ({selectedProducts.length})
                       </motion.button>
                     </motion.div>
                   {selectedOrder.products && parseProducts(selectedOrder.products).length > 0 ? (
                     <div className="space-y-3">
                       {parseProducts(selectedOrder.products).map((product, index) => {
                         const isSelected = selectedProducts.some(p => p.product_id === product.product_id);
                         return (
                           <div key={index} className="border border-purple-200 rounded-xl p-4 bg-white bg-opacity-90 shadow-sm hover:shadow-md transition-all duration-200">
                             <div className="flex items-start justify-between gap-3">
                               <div className="flex items-start gap-3 flex-1">
                                 {/* Checkbox */}
                                 <input
                                   type="checkbox"
                                   checked={isSelected}
                                   onChange={(e) => handleProductSelection(product.product_id, e.target.checked)}
                                   className="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                                 />
                                 
                                 {/* Product Image */}
                                 {product.image_url ? (
                                   <img 
                                     src={product.image_url} 
                                     alt={product.name}
                                     className="w-20 h-20 object-cover rounded-lg border-2 border-purple-200 flex-shrink-0 shadow-sm hover:shadow-md transition-shadow duration-200"
                                     onError={(e) => {
                                       e.target.style.display = 'none';
                                       e.target.nextSibling.style.display = 'flex';
                                     }}
                                   />
                                 ) : null}
                                 
                                 {/* Fallback Image */}
                                 <div 
                                   className="w-20 h-20 bg-gradient-to-br from-purple-100 to-purple-200 rounded-lg border-2 border-purple-200 flex items-center justify-center flex-shrink-0 shadow-sm"
                                   style={{ display: product.image_url ? 'none' : 'flex' }}
                                 >
                                   <span className="text-2xl">📦</span>
                                 </div>
                                 
                                 {/* Product Info */}
                                 <div className="flex-1 min-w-0">
                                   <h5 className="font-semibold text-gray-900 text-sm lg:text-base break-words leading-relaxed">
                                     {product.name || `Product ID: ${product.product_id}`}
                                   </h5>
                                   <div className="mt-2 space-y-1">
                                     <div className="flex flex-wrap items-center gap-3 text-sm">
                                       <span className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                                         💰 {formatCurrency(product.price || 0)}
                                       </span>
                                       <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                                         📦 {product.quantity || product.qty || 1}
                                       </span>
                                     </div>
                                     {product.variation && (
                                       <div className="mt-1">
                                         <span className="text-xs text-purple-600 bg-purple-100 px-2 py-1 rounded">
                                           {product.variation}
                                         </span>
                                       </div>
                                     )}
                                   </div>
                                 </div>
                               </div>
                               
                               <div className="text-right ml-3">
                                 {product.total && (
                                   <div className="text-right">
                                     <p className="font-bold text-blue-600 text-sm lg:text-base">
                                       {formatCurrency(product.total)}
                                     </p>
                                     <p className="text-xs text-gray-500 mt-1">
                                       Tổng
                                     </p>
                                   </div>
                                 )}
                               </div>
                             </div>
                             
                             {/* Design Images Section */}
                             {(() => {
                               const orderId = selectedOrder.order_id || selectedOrder.id;
                               const designCaseData = orderDesignCases[orderId];
                               
                               if (!designCaseData || !designCaseData.cases || designCaseData.cases.length === 0) {
                                 return null;
                               }
                               
                               // Tìm cases liên quan đến product này
                               const relatedCases = designCaseData.cases.filter(case_ => 
                                 case_.product_id === product.product_id
                               );
                               
                               if (relatedCases.length === 0) {
                                 return null;
                               }
                               
                               return (
                                 <div className="mt-3 pt-3 border-t border-purple-200">
                                   <h6 className="text-xs font-medium text-purple-700 mb-2">🎨 Design Images:</h6>
                                   <div className="flex flex-wrap gap-2">
                                     {relatedCases.map((case_, caseIndex) => {
                                       if (!case_.design_files || case_.design_files.length === 0) {
                                         return (
                                           <div key={caseIndex} className="text-xs text-gray-500">
                                             <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getDesignCaseStatusColor(case_.status)}`}>
                                               <span className="mr-1">{getDesignCaseStatusIcon(case_.status)}</span>
                                               <span>{getDesignCaseStatusLabel(case_.status)}</span>
                                             </span>
                                             <span className="ml-2">Chưa có design</span>
                                           </div>
                                         );
                                       }
                                       
                                       return case_.design_files.map((designFile, fileIndex) => (
                                         <div key={`${caseIndex}-${fileIndex}`} className="relative group">
                                           <img
                                             src={designFile}
                                             alt={`Design ${caseIndex + 1} - ${getDesignCaseStatusLabel(case_.status)}`}
                                             className="w-16 h-16 object-cover rounded-lg border-2 border-purple-200 cursor-pointer hover:border-purple-400 transition-all duration-200 hover:scale-105"
                                             onClick={() => handleImageClick(designFile, `Design ${caseIndex + 1} - ${getDesignCaseStatusLabel(case_.status)}`)}
                                             onError={(e) => {
                                               e.target.style.display = 'none';
                                               e.target.nextSibling.style.display = 'flex';
                                             }}
                                           />
                                           <div 
                                             className="w-16 h-16 bg-gradient-to-br from-purple-100 to-purple-200 rounded-lg border-2 border-purple-200 flex items-center justify-center cursor-pointer hover:border-purple-400 transition-all duration-200 hover:scale-105"
                                             style={{ display: 'none' }}
                                             onClick={() => handleImageClick(designFile, `Design ${caseIndex + 1} - ${getDesignCaseStatusLabel(case_.status)}`)}
                                           >
                                             <span className="text-lg">🎨</span>
                                           </div>
                                           <div className="absolute -top-1 -right-1">
                                             <span className={`inline-flex items-center px-1 py-0.5 rounded-full text-xs font-medium border ${getDesignCaseStatusColor(case_.status)}`}>
                                               <span className="text-xs">{getDesignCaseStatusIcon(case_.status)}</span>
                                             </span>
                                           </div>
                                         </div>
                                       ));
                                     })}
                                   </div>
                                 </div>
                               );
                             })()}
                           </div>
                         );
                       })}
                     </div>
                   ) : (
                     <motion.p 
                       initial={{ opacity: 0 }}
                       animate={{ opacity: 1 }}
                       transition={{ duration: 0.3, delay: 0.7 }}
                       className="text-sm text-gray-500 italic"
                     >
                       Không có thông tin sản phẩm chi tiết
                     </motion.p>
                   )}
                 </motion.div>
                 </div>
              </div>
           </motion.div>
         </motion.div>
       )}

       {/* Design Case Modal */}
       {showDesignCaseModal && (
         <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 animate-fadeIn">
           <div className="fixed right-0 top-0 h-full w-full md:w-2/3 lg:w-1/2 xl:w-1/3 bg-white shadow-2xl transform transition-transform duration-300 ease-in-out animate-slideInRight overflow-hidden">
             <div className="flex flex-col h-full">
               {/* Sticky Header */}
               <div className="flex justify-between items-center bg-gradient-to-r from-purple-500 to-purple-600 text-white p-4 shadow-lg sticky top-0 z-10">
                 <h3 className="text-lg font-bold">
                   Tạo Design Case ({selectedProducts.length} sản phẩm)
                 </h3>
                 <button
                   onClick={resetDesignCaseModal}
                   className="p-2 hover:bg-white/20 rounded-full transition-all duration-200 transform hover:scale-110"
                 >
                   ✕
                 </button>
               </div>

               {/* Scrollable Content */}
               <div className="flex-1 overflow-y-auto p-4 space-y-6">
                 {/* Selected Products Summary */}
                 <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
                   <h4 className="text-sm font-medium text-purple-800 mb-3">Sản phẩm đã chọn:</h4>
                   <div className="space-y-2">
                     {selectedProducts.map((product, index) => (
                       <div key={index} className="flex justify-between items-center text-sm bg-white rounded-lg p-2">
                         <span className="text-gray-700">
                           {product.name || `Product ID: ${product.product_id}`}
                         </span>
                         <span className="text-gray-500">
                           Qty: {product.quantity || product.qty || 1}
                         </span>
                       </div>
                     ))}
                   </div>
                 </div>

                 {/* Design Cost */}
                 <div className="bg-white rounded-xl p-4 border border-gray-200">
                   <label className="block text-sm font-medium text-gray-700 mb-2">
                     💰 Chi phí thiết kế (VNĐ)
                   </label>
                   <input
                     type="number"
                     value={designCost}
                     onChange={(e) => setDesignCost(e.target.value)}
                     className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                     placeholder="0"
                     min="0"
                     step="0.01"
                   />
                 </div>

                 {/* Deadline */}
                 <div className="bg-white rounded-xl p-4 border border-gray-200">
                   <label className="block text-sm font-medium text-gray-700 mb-2">
                     📅 Deadline (Tùy chọn)
                   </label>
                   <input
                     type="date"
                     value={deadline}
                     onChange={(e) => setDeadline(e.target.value)}
                     className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                   />
                 </div>

                 {/* Priority */}
                 <div className="bg-white rounded-xl p-4 border border-gray-200">
                   <label className="block text-sm font-medium text-gray-700 mb-2">
                     ⚡ Độ ưu tiên
                   </label>
                   <select
                     value={priority}
                     onChange={(e) => setPriority(e.target.value)}
                     className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                   >
                     <option value="Thấp">🟢 Thấp</option>
                     <option value="Trung bình">🟡 Trung bình</option>
                     <option value="Cao">🟠 Cao</option>
                     <option value="Khẩn cấp">🔴 Khẩn cấp</option>
                   </select>
                 </div>

                 {/* Notes */}
                 <div className="bg-white rounded-xl p-4 border border-gray-200">
                   <label className="block text-sm font-medium text-gray-700 mb-2">
                     📝 Ghi chú (Tùy chọn)
                   </label>
                   <textarea
                     value={notes}
                     onChange={(e) => setNotes(e.target.value)}
                     rows="4"
                     className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                     placeholder="Ghi chú về yêu cầu thiết kế..."
                   />
                 </div>

                 {/* Action Buttons */}
                 <div className="flex gap-3">
                   <button
                     onClick={resetDesignCaseModal}
                     className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 bg-white rounded-lg hover:bg-gray-50 transition-colors"
                     disabled={isCreatingCase}
                   >
                     ❌ Hủy
                   </button>
                   <button
                     onClick={handleSubmitDesignCase}
                     disabled={isCreatingCase || selectedProducts.length === 0}
                     className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                   >
                     {isCreatingCase ? (
                       <>
                         <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                         <span>Đang tạo...</span>
                       </>
                     ) : (
                       <>
                         <span>✏️</span>
                         Tạo Design Case
                       </>
                     )}
                   </button>
                 </div>
               </div>
             </div>
           </div>
         </div>
       )}

       {/* Image Popup */}
       {showImagePopup && (
         <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-[9999]" onWheel={handleImageWheel}>
           <motion.div
             initial={{ opacity: 0, scale: 0.9 }}
             animate={{ opacity: 1, scale: 1 }}
             exit={{ opacity: 0, scale: 0.9 }}
             transition={{ duration: 0.3 }}
             className="relative max-w-4xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden"
           >
             <div className="absolute top-4 right-4 z-10 flex items-center space-x-2">
               <button
                 onClick={() => setImageScale(Math.max(0.5, imageScale - 0.2))}
                 className="p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-75 transition-all duration-300"
                 title="Thu nhỏ"
               >
                 <ZoomOut className="w-5 h-5" />
               </button>
               <button
                 onClick={() => setImageScale(1)}
                 className="px-3 py-2 bg-black bg-opacity-50 text-white rounded-lg hover:bg-opacity-75 transition-all duration-300 text-sm font-medium"
                 title="Khôi phục kích thước"
               >
                 {Math.round(imageScale * 100)}%
               </button>
               <button
                 onClick={() => setImageScale(Math.min(3, imageScale + 0.2))}
                 className="p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-75 transition-all duration-300"
                 title="Phóng to"
               >
                 <ZoomIn className="w-5 h-5" />
               </button>
               <button
                 onClick={() => setShowImagePopup(false)}
                 className="p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-75 transition-all duration-300"
                 title="Đóng"
               >
                 <Close className="w-6 h-6" />
               </button>
             </div>
             <div className="p-4 text-center overflow-auto">
               <h3 className="text-lg font-semibold text-gray-800 mb-4">{selectedImageAlt}</h3>
               <div className="flex justify-center">
                 <img
                   src={selectedImage}
                   alt={selectedImageAlt}
                   className="object-contain rounded-lg transition-transform duration-200"
                   style={{
                     maxWidth: '100%',
                     maxHeight: '70vh',
                     transform: `scale(${imageScale})`,
                     cursor: imageScale > 1 ? 'grab' : 'default'
                   }}
                   onError={(e) => {
                     e.target.src = 'https://via.placeholder.com/400x300?text=Image+Not+Found';
                   }}
                 />
               </div>
               <div className="mt-4 text-sm text-gray-500">
                 <p>Lăn chuột để phóng to/thu nhỏ • Sử dụng nút +/- để điều chỉnh chính xác</p>
               </div>
             </div>
           </motion.div>
         </div>
       )}
     </div>
   );
 };

export default SellerOrders; 